import { FC } from "react";
import SharingDialog, { SharingDialogApi } from "../../components/sharing/SharingDialog";
import useI18n from "../../hooks/useTranslations";
import { useProjectSharingInvitesApi } from "../../http/useProjectSharingInvites";
import { useProjectSharingsApi } from "../../http/useProjectSharings";
import { useSharingRequestsApi } from "../../http/useSharingRequests";

interface ProjectSharingDialogProps {
  projectId: string;
  projectName?: string;
  isAdmin?: boolean;
  onClose: () => void;
}

const ProjectSharingDialog: FC<ProjectSharingDialogProps> = ({ projectId, projectName, isAdmin = false, onClose }) => {
  const i18n = useI18n();
  const projectSharingsApi = useProjectSharingsApi();
  const projectSharingInvitesApi = useProjectSharingInvitesApi();
  const sharingRequestsApi = useSharingRequestsApi("projects");

  const api: SharingDialogApi = {
    useSharings: (query, signal) => projectSharingsApi.useProjectSharings(projectId, query, signal, isAdmin),
    useInvites: (query, signal) => projectSharingInvitesApi.useProjectSharingInvites(projectId, query, signal, isAdmin),
    insertSharing: (body) => projectSharingsApi.insertProjectSharing(projectId, body, isAdmin),
    updateSharing: (sharing, edit) =>
      projectSharingsApi.updateProjectSharing(projectId, sharing.user.id, { edit }, isAdmin),
    deleteSharing: (sharing) => projectSharingsApi.deleteProjectSharing(projectId, sharing.user.id, isAdmin),
    resendInvite: (inviteId) => projectSharingInvitesApi.resendProjectSharingInvite(projectId, inviteId, isAdmin),
    cancelInvite: (inviteId) => projectSharingInvitesApi.cancelProjectSharingInvite(projectId, inviteId, isAdmin),
    deleteInvite: (inviteId) => projectSharingInvitesApi.deleteProjectSharingInvite(projectId, inviteId, isAdmin),
    useRequests: (query, signal) => sharingRequestsApi.useSharingRequests(projectId, query, signal, isAdmin),
    acceptRequest: (requestId, edit) => sharingRequestsApi.acceptSharingRequest(projectId, requestId, edit, isAdmin),
    rejectRequest: (requestId) => sharingRequestsApi.rejectSharingRequest(projectId, requestId, isAdmin),
  };

  return (
    <SharingDialog
      api={api}
      labels={{
        title: projectName
          ? `${i18n.translation.projects.editSharings}: ${projectName}`
          : i18n.translation.projects.editSharings,
        shareWithUser: i18n.translation.projects.shareWithUser,
        invitations: i18n.translation.projects.invitations,
        unshareQuestion: i18n.translation.projects.questions.unshare,
      }}
      onClose={onClose}
    />
  );
};

export default ProjectSharingDialog;
